import React from 'react';
import PropTypes from 'prop-types';
import { snakeCase } from 'lodash';
import { documentToPlainTextString } from '@contentful/rich-text-plain-text-renderer';
import readingTime from 'reading-time';
import { getImage } from 'gatsby-plugin-image';

import {
  Text, ArticleTeaser, Link,
  Internal
} from '@comicrelief/component-library';

import newsDefaultImage from '../../../static/assets/news-default.jpg';
import {
  Wrapper, Grid, Items, HideText
} from './NewsBlock.styles';
import { paddingOptionMapping } from '../../utils/optionMapping';

const NewsBlock = ({
  data,
  relatedArticlesRow = false,
  isPressRelease = false,
  backgroundColor = 'grey light',
  paragraphTitle = "What's going on",
  hideParagraphTitle = null,
  category = null,
  isParagraphFeaturedNews = false,
  paddingAbove,
  paddingBelow
}) => {
  const changeColor = background => (background && (background.includes('dark') || background.includes('red') || background.includes('deep_violet')) ? 'white' : 'black');

  let thisBackgroundColor = backgroundColor;
  let thisParagraphTitle = paragraphTitle;

  if (relatedArticlesRow === true) {
    thisBackgroundColor = 'red';
    thisParagraphTitle = isPressRelease ? 'Related press releases' : 'Related news';
  }

  const isRelatedArticles = data.length !== 0;

  const showMoreNewsButton = !isRelatedArticles
  || (!relatedArticlesRow && category === null) || isParagraphFeaturedNews;

  // Map the human-readable labels to the actual values:
  const paddingTop = paddingAbove ? paddingOptionMapping[paddingAbove] : '2rem';
  const paddingBottom = paddingBelow ? paddingOptionMapping[paddingBelow] : '2rem';

  return (
    <Wrapper
      bgColor={snakeCase(thisBackgroundColor)}
      paddingTop={paddingTop}
      paddingBottom={paddingBottom}
    >
      {thisParagraphTitle && (
        <HideText hideText={hideParagraphTitle || !isRelatedArticles}>
          <Text tag="h2" color={changeColor(snakeCase(thisBackgroundColor))}>
            {thisParagraphTitle}
          </Text>
        </HideText>
      )}
      {isRelatedArticles
      && (
        <Grid>
          {data.map((article) => {
            const {
              title, path, image, displayDate, id, type, body
            } = article.node;

            const content = body && documentToPlainTextString(body.raw);
            const fullPath = type === 'Press Release' ? `/press-releases/${path}` : `/news/${path}`;

            const thisImageData = getImage(image);
            const thisImageFallback = thisImageData?.images?.fallback?.src || newsDefaultImage;
            const thisImageSrcSet = thisImageData?.images?.sources?.[0]?.srcSet
            || thisImageData?.images?.fallback?.srcSet || newsDefaultImage;
            const thisImageLow = thisImageData?.placeholder?.fallback || newsDefaultImage;
            const thisImageDesc = image?.description ?? '';

            const time = content && readingTime(content);

            return (
              <Items key={id}>
                <ArticleTeaser
                  href={fullPath}
                  image={thisImageFallback}
                  images={thisImageSrcSet}
                  imageLow={thisImageLow}
                  date={displayDate}
                  title={title}
                  time={time && time.text}
                  alt={thisImageDesc}
                />
              </Items>
            );
          })}
        </Grid>
      )}
      {showMoreNewsButton && (
        <Link href="/news" type="button" color={isRelatedArticles ? 'red' : 'white'} icon={<Internal colour="white" />}>
          See more
        </Link>
      )}
    </Wrapper>
  );
};

export default NewsBlock;

NewsBlock.propTypes = {
  data: PropTypes.oneOfType([PropTypes.shape, PropTypes.array]).isRequired,
  relatedArticlesRow: PropTypes.bool,
  isPressRelease: PropTypes.bool,
  backgroundColor: PropTypes.string,
  paragraphTitle: PropTypes.string,
  hideParagraphTitle: PropTypes.string,
  category: PropTypes.string,
  isParagraphFeaturedNews: PropTypes.bool,
  paddingAbove: PropTypes.string,
  paddingBelow: PropTypes.string
};
